// src/components/auth/ProtectedRoute.jsx
import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { USER_ROLES } from '../../config/auth0.config';

/**
 * Loading Spinner Component
 */
function LoadingSpinner() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontSize: '1.5rem',
      color: '#666'
    }}>
      Loading...
    </div>
  );
}

/**
 * Protected Route Component
 * Requires authentication
 */
export function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();


  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Check Auth0 authentication state ONLY
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated, redirecting to /');
    // Redirect to home page, save the attempted location
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

/**
 * Role-Based Route Component
 * Requires authentication AND specific role(s)
 */
export function RoleBasedRoute({ children, allowedRoles = [] }) {
  const { isAuthenticated, isLoading } = useAuth0();
  const { role } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check if user has one of the allowed roles
  const hasPermission = allowedRoles.length === 0 || allowedRoles.includes(role);

  if (!hasPermission) {
    // Redirect to unauthorized page or home
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>403</h1>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Access Denied</h2>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          You don't have permission to access this page.
        </p>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          Go to Home
        </button>
      </div>
    );
  }

  return children;
}

/**
 * Admin Route Component
 * Shorthand for admin/super_admin only routes
 */
export function AdminRoute({ children }) {
  return (
    <RoleBasedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}>
      {children}
    </RoleBasedRoute>
  );
}

/**
 * Farmer Route Component
 * Shorthand for farmer only routes
 */
export function FarmerRoute({ children }) {
  return (
    <RoleBasedRoute allowedRoles={[USER_ROLES.FARMER, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}>
      {children}
    </RoleBasedRoute>
  );
}

/**
 * User Route Component
 * Shorthand for regular user routes (USER role)
 */
export function UserRoute({ children }) {
  return (
    <RoleBasedRoute allowedRoles={[USER_ROLES.USER]}>
      {children}
    </RoleBasedRoute>
  );
}
